/**
 * main.ts - Application Entry Point
 * 
 * Initializes the Vue.js 3 application with:
 * - AWS Amplify Auth configuration
 * - Pinia for state management
 * - Vue Router for navigation
 * - Bootstrap 5 CSS and JS
 * - Bootstrap Icons
 * 
 * Requirements: 12.3, 12.4, 12.5
 */
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { Amplify } from 'aws-amplify';
import App from './App.vue';
import router from './router';

// Import Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

// Import Bootstrap Icons CSS
import 'bootstrap-icons/font/bootstrap-icons.css';

// Import Custom Theme (must be after Bootstrap to override)
import './assets/styles/theme.css';

// Note: Bootstrap JS is NOT imported - we use Vue's reactive system
// to control dropdowns and modals instead, avoiding conflicts

// Configure Amplify Auth BEFORE creating the Vue app
// This ensures Cognito is ready when any component needs it
const userPoolId = import.meta.env.VITE_USER_POOL_ID || '';
const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID || '';

if (userPoolId && userPoolClientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
  });
  console.log('Amplify configured in main.ts');
} else {
  console.error('Missing Cognito configuration');
}

// Create Vue application instance
const app = createApp(App);

// Create Pinia store instance
const pinia = createPinia();

// Install plugins
app.use(pinia);
app.use(router);

// Mount the application
app.mount('#app');
